import Box from '@mui/material/Box';
import React from 'react';

import { StructureDetailsTabBodyProps } from './StructureDetailsTabBody.props';
import { Panel } from '../../../components/atoms/Panel';
import { TradingHourList } from '../../../components/organisms/TradingHourList';
import { CapacityList } from '../../../components/templates/CapacityList';
import { StructureAddressContactLayout } from '../../../components/templates/StructureAddressContactLayout';
import { PropertyType } from '../../../configs/enums';
import { ADDRESS_CONTACT_TYPES } from '../../../constants/structure';

/**
 * Two independent columns rather than a grid of rows.
 *
 * A grid ties every panel in a row to the tallest one in it, and Details is far
 * taller than Status & capabilities — which left most of a screen of white space
 * under Status while Trading hours sat alone at the bottom. Columns let each
 * side flow at its own pace, so Address rises to fill that gap.
 *
 * Below `lg` the columns become `display: contents`, which dissolves them and
 * makes every panel a direct child of the outer flex container. The `order`
 * values then apply across all six and give the phone a sensible reading order —
 * Details, Status, Address, Contact, Trading hours, Capacity — rather than one
 * whole column followed by the other. Within a column at `lg` those same orders
 * are already in ascending sequence, so they change nothing there.
 */
const COLUMN_SX = {
  display: { xs: 'contents', lg: 'flex' },
  flexDirection: 'column',
  gap: 1.75,
  flex: 1,
  minWidth: 0,
};

const ORDER = {
  details: { order: 1 },
  status: { order: 2 },
  address: { order: 3 },
  contact: { order: 4 },
  tradingHours: { order: 5 },
  capacity: { order: 6 },
};
export const StructureDetailsTabBody: React.FC<StructureDetailsTabBodyProps> = ({
  propertyTypes,
  isNew,
  structureEditPermissions,
  detailsPanel,
  statusPanel,
  hasLocationContent = true,
  banner,
  unmatchedSuburb,
  addressDataset,
  setAddressDataset,
  addressFormData,
  setAddressFormData,
  editAddressData,
  setEditAddressData,
  addressForm,
  setAddressForm,
  contactDataset,
  setContactDataset,
  contactFormData,
  setContactFormData,
  editContactData,
  setEditContactData,
  contactForm,
  setContactForm,
  tradingHoursData,
  setTradingHoursData,
  capacityData,
  setCapacityData,
  capacityDefaultValue,
}) => {
  // On a new structure every section is editable; on an existing one the
  // permission decides. Same rule the stacked layout used.
  const canEditAddressContact = isNew
    ? true
    : !!structureEditPermissions?.hasContractAddressEditPermission;

  const showAddress = hasLocationContent && propertyTypes.includes(PropertyType.Address);
  const showContact = hasLocationContent && propertyTypes.includes(PropertyType.Contact);
  const showTradingHours = hasLocationContent && propertyTypes.includes(PropertyType.TradingHours);
  const showCapacity =
    hasLocationContent &&
    propertyTypes.includes(PropertyType.Allocations) &&
    !!capacityData &&
    !!setCapacityData &&
    !!capacityDefaultValue;

  const addressPanel = (
    <Panel title="Address" overflow="visible">
      {banner}
      <StructureAddressContactLayout
        flush
        heading=""
        isAddress
        renderNotes={false}
        contactType={ADDRESS_CONTACT_TYPES}
        addressDataset={addressDataset ?? []}
        contactDataset={contactDataset}
        contactFormData={contactFormData}
        addressFormData={addressFormData}
        editAddressFormData={editAddressData}
        onClickAddressEdit={(event, index) => {
          // Build a new array: splice mutates state in place and the setter then
          // receives the same reference, so React's Object.is check can skip the
          // re-render.
          const selectedValue = addressDataset[index];
          setAddressDataset(addressDataset.filter((_, i) => i !== index));
          setEditAddressData(selectedValue);
        }}
        onClickAddressDelete={(event, index) => {
          // Replace the record rather than mutating it: a shallow array copy
          // still points at the same objects, so assigning through it writes
          // into the current state and anything comparing by identity sees no
          // change.
          setAddressDataset(
            addressDataset.map((row, i) => (i === index ? { ...row, isActive: false } : row))
          );
        }}
        onStructureAddressChange={(value) => setAddressFormData(value)}
        handleAddressCreate={(index) => {
          setAddressDataset([...addressDataset, addressFormData[index]]);
          setAddressFormData(addressFormData.filter((_, i) => i !== index));
        }}
        addressForm={addressForm}
        setAddressForm={setAddressForm}
        enableContactNumber
        enableAddressContactFields
        unmatchedSuburb={unmatchedSuburb}
        hasContractAddressEditPermission={canEditAddressContact}
      />
    </Panel>
  );

  const contactPanel = (
    <Panel title="Contact" overflow="visible">
      <StructureAddressContactLayout
        flush
        heading=""
        isAddress={false}
        renderNotes={false}
        contactType={ADDRESS_CONTACT_TYPES}
        addressDataset={addressDataset}
        contactDataset={contactDataset ?? []}
        contactFormData={contactFormData}
        addressFormData={addressFormData}
        editContactFormData={editContactData}
        onClickContactEdit={(event, index) => {
          // ContactDataType and ContactData disagree on optionality; the
          // reconciliation belongs with those types, not this call site.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const selectedValue: any = contactDataset[index];
          setContactDataset(contactDataset.filter((_, i) => i !== index));
          setEditContactData(selectedValue);
        }}
        onClickContactDelete={(event, index) => {
          setContactDataset(
            contactDataset.map((row, i) => (i === index ? { ...row, isActive: false } : row))
          );
        }}
        onStructureAddressChange={() => ''}
        onContactChange={(value) => setContactFormData(value)}
        handleContactCreate={(index) => {
          setContactDataset([...contactDataset, contactFormData[index]]);
          setContactFormData(contactFormData.filter((_, i) => i !== index));
        }}
        contactForm={contactForm}
        setContactForm={setContactForm}
        hasContractAddressEditPermission={canEditAddressContact}
      />
    </Panel>
  );

  const tradingHoursPanel = (
    <Panel title="Trading hours" overflow="visible">
      <TradingHourList
        flush
        heading=""
        tradingHourValue={tradingHoursData}
        onTradingHoursChange={(value) => setTradingHoursData(value)}
        onTradingHoursCreate={(value) => setTradingHoursData(value)}
        onTradingHoursDelete={(index) => {
          const updatedData = [...tradingHoursData];
          if (index > -1) {
            updatedData.splice(index, 1);
          }
          setTradingHoursData([...updatedData]);
        }}
        hasTradingHoursEditPermission={
          isNew ? true : !!structureEditPermissions?.hasTradingHoursEditPermission
        }
      />
    </Panel>
  );

  const capacityPanel = showCapacity && capacityData && setCapacityData && capacityDefaultValue && (
    <Panel title="Capacity" overflow="visible">
      <CapacityList
        flush
        heading=""
        capacityValue={capacityData}
        capacityDefaultValue={capacityDefaultValue}
        onCapacityChange={(value) => setCapacityData(value)}
        onCapacityCreate={(value) => setCapacityData(value)}
        onCapacityDelete={(index) => {
          const updatedData = [...capacityData];
          if (index > -1) {
            updatedData.splice(index, 1);
          }
          setCapacityData([...updatedData]);
        }}
        hasCapacityEditPermission={
          isNew ? true : !!structureEditPermissions?.hasCapacityEditPermission
        }
      />
    </Panel>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: 'start',
        gap: 1.75,
      }}
    >
      <Box sx={COLUMN_SX}>
        <Box sx={ORDER.details}>{detailsPanel}</Box>
        {showContact && <Box sx={ORDER.contact}>{contactPanel}</Box>}
        {capacityPanel && <Box sx={ORDER.capacity}>{capacityPanel}</Box>}
      </Box>

      <Box sx={COLUMN_SX}>
        {statusPanel && <Box sx={ORDER.status}>{statusPanel}</Box>}
        {showAddress && <Box sx={ORDER.address}>{addressPanel}</Box>}
        {showTradingHours && <Box sx={ORDER.tradingHours}>{tradingHoursPanel}</Box>}
      </Box>
    </Box>
  );
};
